/**
 * App theme provider — wraps the @obrava/ui ThemeProvider, emits the CSS custom
 * properties stylesheet once, and tracks the active scheme.
 *
 * Default scheme follows `prefers-color-scheme`; a manual toggle overrides it
 * and persists in localStorage. Light + dark are mandatory (CLAUDE.md): the
 * switch only swaps token VALUES, never component code.
 */

import React from 'react';
import {
  ThemeProvider as UiThemeProvider,
  themeStylesheet,
  type ThemeName,
} from '@obrava/ui';

const STORAGE_KEY = 'obrava.platform-console.theme';

/** User-facing appearance preference: an explicit scheme, or follow the OS. */
export type ThemePreference = ThemeName | 'system';

interface ThemeControl {
  /** The resolved scheme actually applied (system preference resolved). */
  readonly scheme: ThemeName;
  /** The user's stored preference: 'light' | 'dark' | 'system'. */
  readonly preference: ThemePreference;
  readonly toggle: () => void;
  readonly setScheme: (name: ThemeName) => void;
  /** Set the appearance preference; 'system' clears the manual override. */
  readonly setPreference: (pref: ThemePreference) => void;
}

const ThemeControlContext = React.createContext<ThemeControl | null>(null);

function readStored(): ThemeName | null {
  const v = localStorage.getItem(STORAGE_KEY);
  return v === 'light' || v === 'dark' ? v : null;
}

function systemScheme(): ThemeName {
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export function AppThemeProvider(props: {
  readonly children: React.ReactNode;
}): React.ReactElement {
  const [manual, setManual] = React.useState<ThemeName | null>(() => readStored());
  const [system, setSystem] = React.useState<ThemeName>(() => systemScheme());

  // Inject the theme stylesheet once.
  React.useEffect(() => {
    const id = 'obrava-theme-vars';
    if (document.getElementById(id)) return;
    const style = document.createElement('style');
    style.id = id;
    style.textContent = themeStylesheet();
    document.head.appendChild(style);
  }, []);

  // Track the OS scheme so the default follows the system when not overridden.
  React.useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (): void => setSystem(systemScheme());
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const scheme: ThemeName = manual ?? system;

  // Reflect the active scheme on the document root for the CSS-var swap.
  React.useEffect(() => {
    document.documentElement.dataset.theme = scheme;
    document.documentElement.style.colorScheme = scheme;
  }, [scheme]);

  const setScheme = React.useCallback((name: ThemeName) => {
    setManual(name);
    localStorage.setItem(STORAGE_KEY, name);
  }, []);

  const setPreference = React.useCallback((pref: ThemePreference) => {
    if (pref === 'system') {
      setManual(null);
      localStorage.removeItem(STORAGE_KEY);
      return;
    }
    setManual(pref);
    localStorage.setItem(STORAGE_KEY, pref);
  }, []);

  const control = React.useMemo<ThemeControl>(
    () => ({
      scheme,
      preference: manual ?? 'system',
      toggle: () => setScheme(scheme === 'dark' ? 'light' : 'dark'),
      setScheme,
      setPreference,
    }),
    [scheme, manual, setScheme, setPreference],
  );

  return (
    <ThemeControlContext.Provider value={control}>
      <UiThemeProvider name={scheme}>{props.children}</UiThemeProvider>
    </ThemeControlContext.Provider>
  );
}

export function useThemeControl(): ThemeControl {
  const ctx = React.useContext(ThemeControlContext);
  if (!ctx) {
    throw new Error('useThemeControl must be used within AppThemeProvider');
  }
  return ctx;
}
